/* eslint-disable import/no-unresolved */
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CheckOutlined, DownOutlined, PushpinOutlined, InfoCircleFilled } from '@ant-design/icons';
import { Button, Menu, message, Tooltip } from 'antd';
import { cloneDeep, get } from 'lodash';
import { useViewerStore, actions } from '@store/viewerModule/base';
import dimensionsSelector from '@store/common/dimensionList/selectors';
import { updateDashboardById } from '@store/dashboards/update/actions';
import generateUUid from '@utils/generateUUID';
import { Dropdown } from '@components/common';
import commonSelector, { selectedCustomerIdSelector } from '@store/common/selectors';
import vmSelector from '@store/viewerModule/selectors';
import featureNames from '@utils/featureToggle/constants';
import { CDN_ACCOUNT_TYPE, PAGE_TYPE } from '@utils/constants';
import metricSelectorStyles from '../../dimensions-panel/styles/MetricSelector.less';
import {
  columnsMetrics as defaultColumnsMetrics,
  MAX_ANCHORED_COUNT,
  GROUPBY_MAPPING,
  replaceKey,
  getColumnsNameReflection,
} from './utils';
import { getCellRenderer } from './columns';
import { AnchorIcon, ItemContent, Styled_ViewerTableTitle, Styled_IconWrapper } from './styled';
import { getSharingState } from '../common/utils';

const endedPlayText =
  'Ended Session value “No” indicates that the session is still active and end time is not yet recorded. Ended Session is different from Ended Plays metric. Check Learning Centre for more details.';

const compare = (columnsNameReflection) => (a, b) =>
  columnsNameReflection[a].toLowerCase() < columnsNameReflection[b].toLowerCase() ? -1 : 1;

// const isTag = (dimension) =>
//   dimension.field === "TAG" || dimension.field.startsWith("m3.");

const mapDimensionGroupBy = (dimension) => {
  // if (isTag(dimension)) {
  //   return `tags.${dimension.groupBy}`;
  // }
  return get(GROUPBY_MAPPING, dimension.groupBy, dimension.groupBy);
};

const MetricDropMenu = ({ columnsMetrics, customerAccountType, columnsNameReflections }) => {
  const store = useSelector(vmSelector);
  const [vmStore, dispatch] = useViewerStore();
  const reduxDispatch = useDispatch();

  const { anchoredColumns, pageType } = vmStore;
  const selectedColumns = store.base.selectedColumns;
  const [innerSelectedMetrics, changeInnerSelectedMetrics] = React.useState(
    cloneDeep(selectedColumns),
  );
  const { features, interval: commonInterval } = useSelector(commonSelector);
  const selectedCustomerId = useSelector(selectedCustomerIdSelector);
  const timelineFlag = features[featureNames.VIEWER_DRILLDOWN_TIMELINE];
  const [visible, changeVisible] = React.useState(false);
  const [selectedMetricsCount, setSelectedMetricsCount] = React.useState(selectedColumns.length);

  const generateNewColumns = () =>
    selectedColumns
      .filter((item) => item.show)
      .map((item) => ({
        dataKey: replaceKey(item.metric),
        frozen: anchoredColumns[item.metric] ? 'left' : false,
        title: columnsNameReflections[item.metric],
        width: 200,
        height: 60,
        resizable: true,
        sortable: true,
        maxWidth: 340,
        minWidth: 60,
        key: generateUUid(),
        headerRenderer: ({ column }) => {
          if (column.dataKey === 'IsEnded') {
            return (
              <Styled_ViewerTableTitle>
                {' '}
                {column.title}
                <Tooltip placement="top" title={endedPlayText}>
                  <Styled_IconWrapper>
                    <InfoCircleFilled />
                  </Styled_IconWrapper>
                </Tooltip>
              </Styled_ViewerTableTitle>
            );
          } else {
            return <Styled_ViewerTableTitle>{column.title}</Styled_ViewerTableTitle>;
          }
        },
        cellRenderer: ({ column, rowData, cellData }) =>
          getCellRenderer(item.metric, column, rowData, cellData),
      }));

  useEffect(() => {
    changeInnerSelectedMetrics(cloneDeep(selectedColumns));
    setSelectedMetricsCount(selectedColumns.length);
    let tableColumns = generateNewColumns();
    // hide cdn dimension for cdn partner accounts
    if (customerAccountType === CDN_ACCOUNT_TYPE) {
      tableColumns = tableColumns.filter((tableColumn) => tableColumn.dataKey !== 'IntervalCDN');
    }

    dispatch(actions.setTableColumns({ tableColumns }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColumns, anchoredColumns]);

  const renderMenus = () => {
    const keys = Object.keys(columnsMetrics);
    const menuGroups = keys.map((key) => {
      const groupItems = columnsMetrics[key]
        .filter((v) => {
          // If user does not have timeline permission, we should hidden it on dropmenu
          if (v === 'SessionId' && !timelineFlag) {
            return false;
          }
          return true;
        })
        .map((item) => {
          const isSelected = innerSelectedMetrics.find((m) => m.metric === item && m.show);
          const isDisabled = item === 'SessionId' ? true : false;
          const isApplied = selectedColumns.find((m) => m.metric === item && m.show);
          return (
            <Menu.Item
              key={item}
              disabled={isDisabled || (innerSelectedMetrics.length === 1 && isSelected)}
            >
              {isSelected && <CheckOutlined />}
              <ItemContent>{columnsNameReflections[item]}</ItemContent>
              {isApplied && (
                <AnchorIcon
                  component={<PushpinOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    const anchoredMetricsCount = Object.keys(anchoredColumns).filter(
                      (metricKey) => anchoredColumns[metricKey],
                    ).length;
                    if (anchoredMetricsCount === MAX_ANCHORED_COUNT && !anchoredColumns[item]) {
                      message.warning(`Can not pin more than ${MAX_ANCHORED_COUNT} columns`);
                      return;
                    }
                    dispatch(actions.setAnchoredColumns({ anchoredColumns: item }));
                  }}
                  rotate={anchoredColumns[item] ? -45 : 0}
                />
              )}
            </Menu.Item>
          );
        });

      return (
        <Menu.ItemGroup key={key} title={key}>
          {groupItems}
        </Menu.ItemGroup>
      );
    });

    return (
      <Menu
        className={metricSelectorStyles.metricMenu}
        multiple
        onClick={({ key }) => {
          const index = innerSelectedMetrics.findIndex((item) => item.metric === key);
          let newCount = selectedMetricsCount;
          if (index !== -1) {
            const newData = [...innerSelectedMetrics];
            newData[index].show = !innerSelectedMetrics[index].show;
            changeInnerSelectedMetrics(newData);
            if (newData[index].show) {
              newCount += 1;
            } else {
              newCount -= 1;
            }
          } else {
            changeInnerSelectedMetrics([...innerSelectedMetrics, { metric: key, show: true }]);
            newCount += 1;
          }
          setSelectedMetricsCount(newCount);
        }}
      >
        {menuGroups}
      </Menu>
    );
  };

  const applySelectedMetrics = useCallback(() => {
    // below code will clean the already selected column from persistence, if it is not valid anymore.
    // and query/filter for metrics which are only available in the dropdown
    const allColumns = Object.values(columnsMetrics).reduce((acc, cur) => {
      return [...acc, ...cur];
    }, []);
    const selectedColumns = innerSelectedMetrics.filter((col) => allColumns.includes(col.metric));

    reduxDispatch(
      actions.setSelectedColumns({
        selectedColumns,
        // If it's a saved or sharing VM dashboard, shouldn't update the persistence.
        notUpdatePersistence: [PAGE_TYPE.SAVED_DASHBOARD, PAGE_TYPE.SHARING].includes(pageType),
      }),
    );
    dispatch(actions.setSelectedColumns({ selectedColumns }));

    // Need to save the selected columns to saved dashboard config.
    if (PAGE_TYPE.SAVED_DASHBOARD === pageType) {
      // First get the next dashboard config.
      const { state: nextDashboardConfig } = getSharingState(
        vmStore.viewerId,
        vmStore.interval,
        commonInterval,
        selectedCustomerId,
        selectedColumns.filter((it) => it.show).map((it) => it.metric),
      );

      // Dispatch the latest changes to save to persistence configs.
      reduxDispatch(updateDashboardById(vmStore.id, undefined, nextDashboardConfig));
    }

    changeVisible(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerSelectedMetrics]);

  return (
    <Dropdown
      overlay={renderMenus()}
      trigger={['click']}
      disableBtn={selectedMetricsCount === 0}
      visible={visible}
      onVisibleChange={changeVisible}
      onClickBtn={applySelectedMetrics}
      btnName="Apply"
      multiSelect
    >
      <Button className="mx-2" id="metrics-dropdown-multi-select">
        Customize
        <DownOutlined />
      </Button>
    </Dropdown>
  );
};

const MetricDropMenuWrapper = () => {
  const dimensionData = useSelector(dimensionsSelector);
  const { response: dimensions } = dimensionData;
  const customerAccountType = get(useSelector(commonSelector), [
    'features',
    featureNames.CUSTOMER_ACCOUNT_TYPE,
  ]);
  const columnsMetrics = cloneDeep(defaultColumnsMetrics);
  const basics = columnsMetrics['Basic Information'];

  const columnsNameReflections = {
    ...getColumnsNameReflection(),
    ...Object.fromEntries(
      dimensions
        .filter((d) => !basics.includes(mapDimensionGroupBy(d)))
        .map((d) => [mapDimensionGroupBy(d), d.name]),
    ),
  };

  // hide cdn dimension for cdn partner accounts
  if (customerAccountType === CDN_ACCOUNT_TYPE && columnsMetrics['Interval Playback Metrics']) {
    columnsMetrics['Interval Playback Metrics'] = columnsMetrics[
      'Interval Playback Metrics'
    ].filter((dimension) => dimension !== 'IntervalCDN');
  }

  columnsMetrics['Basic Information'] = [
    ...basics,
    ...dimensions
      .filter((d) => !basics.includes(mapDimensionGroupBy(d)))
      .map((d) => mapDimensionGroupBy(d))
      .filter((d) => d !== 'IntervalCDN') // CDN is in the Interval Playback Metrics section already
      .sort(compare(columnsNameReflections)),
  ];

  return (
    <MetricDropMenu
      columnsMetrics={columnsMetrics}
      customerAccountType={customerAccountType}
      columnsNameReflections={columnsNameReflections}
    />
  );
};

export default MetricDropMenuWrapper;
